/**
 * IconSelector Component
 * Allows users to select from predefined icon assets
 */

import { useState } from 'react'

interface PresetIcon {
  label: string
  path: string
  category: 'parking' | 'general'
}

const PRESET_ICONS: PresetIcon[] = [
  // Parking-specific icons
  { label: '조명 CCTV', path: '/assets/parking/lightCctv.svg', category: 'parking' },
  { label: '단방향 CCTV', path: '/assets/parking/warningCctv.svg', category: 'parking' },
  { label: '주차', path: '/assets/parking/parking.svg', category: 'parking' },
  { label: '엘리베이터', path: '/assets/parking/elevator.svg', category: 'parking' },
  { label: '전기 충전기', path: '/assets/parking/charger.svg', category: 'parking' },
  { label: '비상벨', path: '/assets/parking/bell.svg', category: 'parking' },
  { label: '표지판', path: '/assets/parking/sign.svg', category: 'parking' },

  // General icons
  { label: 'CCTV', path: '/assets/cctv.svg', category: 'general' },
  { label: '조명', path: '/assets/light.svg', category: 'general' },
  { label: '엘리베이터', path: '/assets/elevator.svg', category: 'general' },
  { label: '충전기', path: '/assets/charger.svg', category: 'general' },
  { label: '전기차', path: '/assets/ev.svg', category: 'general' },
  { label: '배터리', path: '/assets/battery.svg', category: 'general' },
  { label: '자전거', path: '/assets/bike.svg', category: 'general' },
  { label: '장애인', path: '/assets/handicap.svg', category: 'general' },
  { label: '소형차', path: '/assets/small_car.svg', category: 'general' },
  { label: '경고', path: '/assets/warning.svg', category: 'general' },
  { label: '에러', path: '/assets/error.svg', category: 'general' },
  { label: '마커', path: '/assets/marker.svg', category: 'general' },
  { label: '방지등', path: '/assets/preventionLights.svg', category: 'general' },
]

interface IconSelectorProps {
  selectedIcon: string
  onSelect: (path: string) => void
  onClose?: () => void
}

export function IconSelector({ selectedIcon, onSelect, onClose }: IconSelectorProps) {
  const [filter, setFilter] = useState<'all' | 'parking' | 'general'>('all')

  const filteredIcons = PRESET_ICONS.filter(icon =>
    filter === 'all' || icon.category === filter
  )

  return (
    <div style={{
      marginBottom: '16px',
      border: '1px solid var(--local-border)',
      borderRadius: '12px',
      padding: '16px',
      background: 'var(--local-surface)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
      }}>
        <h4 style={{ margin: 0, fontSize: '13px', color: 'var(--local-text)' }}>
          프리셋 아이콘 선택
        </h4>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--local-text-secondary)',
              cursor: 'pointer',
              fontSize: '18px',
              padding: 0
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* Category Filter */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '12px'
      }}>
        <button
          onClick={() => setFilter('all')}
          style={{
            padding: '4px 12px',
            fontSize: '12px',
            border: '1px solid var(--local-border)',
            borderRadius: '6px',
            background: filter === 'all' ? 'var(--local-accent)' : 'var(--local-surface-hover)',
            color: filter === 'all' ? 'white' : 'var(--local-text)',
            cursor: 'pointer'
          }}
        >
          전체
        </button>
        <button
          onClick={() => setFilter('parking')}
          style={{
            padding: '4px 12px',
            fontSize: '12px',
            border: '1px solid var(--local-border)',
            borderRadius: '6px',
            background: filter === 'parking' ? 'var(--local-accent)' : 'var(--local-surface-hover)',
            color: filter === 'parking' ? 'white' : 'var(--local-text)',
            cursor: 'pointer'
          }}
        >
          주차장
        </button>
        <button
          onClick={() => setFilter('general')}
          style={{
            padding: '4px 12px',
            fontSize: '12px',
            border: '1px solid var(--local-border)',
            borderRadius: '6px',
            background: filter === 'general' ? 'var(--local-accent)' : 'var(--local-surface-hover)',
            color: filter === 'general' ? 'white' : 'var(--local-text)',
            cursor: 'pointer'
          }}
        >
          일반
        </button>
      </div>

      {/* Icon Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
        gap: '8px',
        maxHeight: '300px',
        overflowY: 'auto'
      }}>
        {filteredIcons.map((icon) => (
          <button
            key={icon.path}
            onClick={() => onSelect(icon.path)}
            title={icon.label}
            style={{
              padding: '8px',
              border: selectedIcon === icon.path
                ? '2px solid var(--local-accent)'
                : '1px solid var(--local-border)',
              borderRadius: '8px',
              background: selectedIcon === icon.path
                ? 'var(--local-surface-hover)'
                : 'var(--local-surface)',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              transition: 'all 0.2s'
            }}
          >
            <img
              src={icon.path}
              alt={icon.label}
              style={{
                width: '32px',
                height: '32px',
                objectFit: 'contain'
              }}
              onError={(e) => {
                // Hide broken images
                (e.target as HTMLImageElement).style.display = 'none'
              }}
            />
            <span style={{
              fontSize: '10px',
              color: 'var(--local-text-secondary)',
              textAlign: 'center',
              wordBreak: 'keep-all'
            }}>
              {icon.label}
            </span>
          </button>
        ))}
      </div>

      <div style={{
        marginTop: '12px',
        fontSize: '11px',
        color: 'var(--local-text-secondary)',
        textAlign: 'center'
      }}>
        {filteredIcons.length}개의 아이콘 • 클릭하여 선택
      </div>
    </div>
  )
}
